import { isNil, uniq } from 'lodash-es';

import { z } from 'zod';
import type { CheckFn } from 'zod/v4/core';
import { Department } from '../../referential/Department';
import { LegalContext } from '../../referential/LegalContext';
import { Region } from '../../referential/Region';
import { SSD2Id } from '../../referential/Residue/SSD2Id';
import { maestroDateRefined, toMaestroDate } from '../../utils/date';
import { isDefined } from '../../utils/utils';
import { checkSchema } from '../../utils/zod';
import { Company } from '../Company/Company';
import { CompanyWorkshop } from '../Company/CompanyWorkshop';
import { Geolocation } from '../Geolocation/Geolocation';
import {
  Context,
  OutsideProgrammingPlanContext,
  ProgrammingPlanContext
} from '../ProgrammingPlan/Context';
import {
  analysisPermissionRoles,
  type ProgrammingSubPlan,
  ProgrammingSubPlanId
} from '../ProgrammingPlan/ProgrammingSubPlan';
import { SpecificData, UnknownValue } from '../SpecificData/SpecificData';
import { hasPermission, Sampler, type UserBase } from '../User/User';
import type { UserRole } from '../User/UserRole';
import { SampleCompliance } from './SampleCompliance';
import { PartialSampleItem, SampleItem } from './SampleItem';
import { PartialSampleMatrix, SampleMatrix } from './SampleMatrix';
import { SampleStatus } from './SampleStatus';
import { SampleStep } from './SampleStep';
import { Seves, SevesNotice } from './Seves';

export const SampleContextData = z.object({
  id: z.guid(),
  sampler: Sampler,
  additionalSampler: Sampler.nullish(),
  geolocation: Geolocation.nullish(),
  department: Department.nullish(),
  parcel: z.string().nullish(),
  programmingPlanId: z.guid(),
  programmingSubPlanId: ProgrammingSubPlanId,
  context: Context,
  legalContext: LegalContext.nullish(),
  company: Company.nullish(),
  companyWorkshop: CompanyWorkshop.nullish(),
  companyOffline: z.string().nullish(),
  resytalId: z.string().nullish(),
  notesOnCreation: z.string().nullish(),
  step: SampleStep,
  status: SampleStatus,
  specificData: SpecificData
});

export const SampleMatrixData = z.object({
  matrices: z
    .array(SampleMatrix, {
      error: (issue) =>
        isNil(issue.input) ? 'Veuillez renseigner la matrice.' : issue.message
    })
    .min(1, { message: 'Veuillez renseigner la matrice.' }),
  notesOnMatrix: z.string().nullish(),
  prescriptionId: z.guid().nullish(),
  monoSubstances: z.array(SSD2Id).nullish(),
  multiSubstances: z.array(SSD2Id).nullish(),
  documentIds: z.array(z.guid()).nullish(),
  specificData: SpecificData
});

const outsideProgrammingPlanMatricesCheck: CheckFn<{
  context: Context;
  matrices: SampleMatrix[];
}> = (ctx) => {
  if (
    OutsideProgrammingPlanContext.safeParse(ctx.value.context).success &&
    ctx.value.matrices.length > 1
  ) {
    ctx.issues.push({
      input: ctx.value,
      code: 'custom',
      message: 'Une seule matrice est attendue hors programmation.',
      path: ['matrices']
    });
  }
};

const unknownValueCheck: CheckFn<{
  specificData: SpecificData;
}> = (ctx) => {
  const specificData = ctx.value.specificData;

  Object.entries(specificData).forEach(([key, value]) => {
    if (value === UnknownValue) {
      ctx.issues.push({
        input: ctx.value,
        code: 'custom',
        message: `Veuillez renseigner le descripteur manquant.`,
        path: ['specificData', key]
      });
    }
  });
};

export const sampleSendCheck: CheckFn<
  Pick<SampleBase, 'sampledDate' | 'sentAt' | 'specificData'>
> = (ctx) => {
  if (
    !isNil(ctx.value.sampledDate) &&
    !isNil(ctx.value.sentAt) &&
    ctx.value.sampledDate > toMaestroDate(ctx.value.sentAt)
  ) {
    ctx.issues.push({
      input: ctx.value,
      code: 'custom',
      message:
        "La date de prélèvement ne peut pas être postérieure à la date d'envoi au laboratoire.",
      path: ['sampledDate']
    });
  }

  unknownValueCheck(ctx);
};

export const prescriptionSubstancesCheck: CheckFn<{
  prescriptionId?: string | null;
  monoSubstances?: SSD2Id[] | null;
  multiSubstances?: SSD2Id[] | null;
}> = (ctx) => {
  if (!isDefined(ctx.value.prescriptionId)) {
    if (isNil(ctx.value.monoSubstances) && isNil(ctx.value.multiSubstances)) {
      ctx.issues.push({
        input: ctx.value,
        code: 'custom',
        message:
          'Veuillez préciser les substances mono-résidu et/ou multi-résidus à analyser.',
        path: ['substances']
      });
    }
    if (
      !isNil(ctx.value.monoSubstances) &&
      ctx.value.monoSubstances.length === 0
    ) {
      ctx.issues.push({
        input: ctx.value,
        code: 'custom',
        message: 'Veuillez renseigner au moins une substance mono-résidu.',
        path: ['monoSubstances']
      });
    }
  }
};

export const sampleItemSealIdCheck: CheckFn<{
  items: PartialSampleItem[];
}> = (ctx) => {
  const sealsIds = ctx.value.items
    .map((item) => item.sealId)
    .filter((sealId) => !isNil(sealId) && sealId.trim() !== '');
  if (uniq(sealsIds).length !== sealsIds.length) {
    ctx.issues.push({
      input: ctx.value,
      code: 'custom',
      message: 'Les numéros de scellés doivent être uniques.',
      path: ['items']
    });
  }
};

export const SampleItemsDataChecked = checkSchema(
  z.object({
    sampledDate: maestroDateRefined,
    sampledTime: z.string().regex(/^\d{2}:\d{2}$/, {
      error: () => "L'heure de prélèvement est invalide."
    }),
    items: z
      .array(SampleItem)
      .min(1, { message: 'Veuillez renseigner au moins un échantillon.' }),
    notesOnItems: z.string().nullish()
  }),
  sampleItemSealIdCheck
);

export const SampleOwnerData = z.object({
  ownerFirstName: z.string().nullish(),
  ownerLastName: z.string().nullish(),
  ownerEmail: z.email("L'adresse email du détenteur est invalide.").nullish(),
  ownerAgreement: z.boolean({
    error: (issue) =>
      isNil(issue.input)
        ? "Veuillez renseigner l'accord du détenteur."
        : issue.message
  }),
  notesOnOwnerAgreement: z.string().nullish()
});

const PartialSampleMatrixData = z.object({
  ...SampleMatrixData.partial().shape,
  matrices: z.array(PartialSampleMatrix).nullish(),
  specificData: SpecificData
});

export const PartialSampleToCreate = z.object({
  ...SampleContextData.partial().required({
    id: true,
    programmingPlanId: true,
    step: true,
    status: true,
    sampler: true
  }).shape,
  ...PartialSampleMatrixData.shape,
  ...z.object(SampleItemsDataChecked.shape).partial().shape,
  ...SampleOwnerData.partial().shape,
  items: z.array(PartialSampleItem).nullish()
});

export const SampleToCreate = z.object({
  ...SampleContextData.shape,
  ...SampleMatrixData.shape,
  ...SampleItemsDataChecked.shape,
  ...SampleOwnerData.shape
});

export const CreatedSampleData = z.object({
  reference: z.string(),
  region: Region,
  createdAt: z.coerce.date(),
  lastUpdatedAt: z.coerce.date(),
  seves: Seves.nullish(),
  sevesNotice: SevesNotice.nullish()
});

export const SampleComplianceData = z.object({
  compliance: SampleCompliance.nullish(),
  notesOnCompliance: z.string().nullish()
});

export const PartialSample = PartialSampleToCreate.extend({
  ...CreatedSampleData.shape,
  ...SampleComplianceData.partial().shape,
  sentAt: z.coerce.date().nullish()
});

export const SampleBase = SampleToCreate.extend({
  ...CreatedSampleData.shape,
  ...SampleComplianceData.shape,
  geolocation: Geolocation,
  department: Department,
  company: Company,
  items: z.array(SampleItem),
  sentAt: z.coerce.date().nullish()
});

const sampleItemsCheck: CheckFn<{ items: SampleItem[] }> = (ctx) => {
  ctx.value.items.forEach((item, index) => {
    if (item.itemNumber !== 1 && !isNil(item.complianceOverride)) {
      ctx.issues.push({
        input: ctx.value,
        code: 'custom',
        path: ['items', index, 'complianceOverride']
      });
    }
  });
};

export const SampleChecked = checkSchema(
  SampleBase,
  prescriptionSubstancesCheck,
  outsideProgrammingPlanMatricesCheck,
  sampleItemsCheck
);

export type SampleContextData = z.infer<typeof SampleContextData>;
export type SampleMatrixData = z.infer<typeof SampleMatrixData>;
export type SampleItemsDataChecked = z.infer<typeof SampleItemsDataChecked>;
export type SampleOwnerData = z.infer<typeof SampleOwnerData>;
export type CreatedSampleData = z.infer<typeof CreatedSampleData>;
export type SampleComplianceData = z.infer<typeof SampleComplianceData>;
export type PartialSampleToCreate = z.infer<typeof PartialSampleToCreate>;
export type PartialSample = z.infer<typeof PartialSample>;
export type SampleToCreate = z.infer<typeof SampleToCreate>;
export type SampleBase = z.infer<typeof SampleBase>;
export type SampleChecked = z.infer<typeof SampleChecked>;

export const isCreatedPartialSample = (
  partialSample?: PartialSample | PartialSampleToCreate
): partialSample is PartialSample =>
  partialSample !== undefined &&
  CreatedSampleData.safeParse(partialSample).success;

const DeletableSampleStatusList: SampleStatus[] = ['Draft', 'Submitted'];

export const isDeletableSample = (
  sample: Pick<PartialSampleToCreate, 'status'>
) => DeletableSampleStatusList.includes(sample.status);

export const isProgrammingPlanSample = (
  partialSample?: PartialSample | PartialSampleToCreate
) => ProgrammingPlanContext.safeParse(partialSample?.context).success;

export const isOutsideProgrammingPlanSample = (
  partialSample?: PartialSample | PartialSampleToCreate
) => OutsideProgrammingPlanContext.safeParse(partialSample?.context).success;

export const SamplePermission = z.enum(['performAnalysis']);

export type SamplePermission = z.infer<typeof SamplePermission>;

export const hasSamplePermission = (
  user: Pick<UserBase, 'region'>,
  userRole: UserRole,
  sample: Pick<SampleBase, 'region'>,
  subPlan?: Pick<ProgrammingSubPlan, 'subPlanNumber' | 'stages'> | null
): Record<SamplePermission, boolean> => ({
  performAnalysis:
    hasPermission(userRole, 'performAnalysis') &&
    sample.region === user.region &&
    !isNil(subPlan) &&
    analysisPermissionRoles(subPlan).includes(userRole)
});
